<script setup lang="ts">
import Page404Shell from '@pkg/site-chrome/components/Page404.vue';
import { DOCUMENT } from '@pkg/config/sanity/schemas';
import { SINGLETON_DOCUMENT } from '@pkg/config/sanity/singletons';
import { singletonDocId, singletonDocIdByName } from '@pkg/sanity-utils/singletonDocId';
import type { LOCALE_CODE } from '@pkg/config/languages/localeCodes';
import ModularBlocksRenderer from '@pkg/modular-blocks/frontend/ModularBlocksRenderer.vue';
import { useSanityPageSeoMeta } from '@pkg/sanity-utils/composables/useSanityPageSeoMeta';
import {
  businessSoft404Page,
  type BusinessPageByIdResult,
} from '~/apis/sanity/queries/pageById';

/**
 * Soft-404 for the business site — same pattern as apps/web Page404:
 * load BUSINESS_PAGE_404 (fallback: WWW PAGE_404), set HTTP 404 via shared shell,
 * render modular outer blocks.
 */
const { locale } = useI18n();
const localeCode = unref(locale) as LOCALE_CODE;
const pageId = singletonDocIdByName(SINGLETON_DOCUMENT.BUSINESS_PAGE_404, localeCode);
const fallbackPageId = singletonDocId(
  DOCUMENT.PAGES,
  localeCode,
  SINGLETON_DOCUMENT.PAGE_404,
);

const { data } = await useFetch<BusinessPageByIdResult>('/api/sanity-fetch', {
  method: 'POST',
  body: {
    query: businessSoft404Page,
    params: {
      id: pageId,
      fallbackId: fallbackPageId,
      // Required by modular blocks (FAQ, translated fields, portable-text refs).
      locale: localeCode,
    },
  },
  key: `business-404-${pageId}`,
});

const { data: manifest } = await useSiteManifestQuery();

useSanityPageSeoMeta(() => data.value?.page?.seoSocial);

const missingPageTitle = () => {
  if (data.value?.page?.seoSocial?.metaTitle?.trim()) {
    return undefined;
  }
  return data.value?.page?.title?.trim() || 'Page not found';
};

useSeoMeta({
  title: missingPageTitle,
  ogTitle: () => (manifest.value?.defaultSeo ? undefined : missingPageTitle()),
});
</script>

<template>
  <Page404Shell>
    <ModularBlocksRenderer
      v-if="data?.page?.content?.length"
      :blocks="data.page.content"
      type="outer"
    />
    <div
      v-else
      class="wrapper py-64 desk:py-96"
    >
      <h1 class="text-h2 text-primary mb-16">
        Page not found
      </h1>
      <p class="text-body text-primary-30">
        Publish the Business
        <code>404 Page</code>
        singleton in Studio for this locale
        (<code>{{ pageId }}</code>),
        or ensure the WWW
        <code>page-404</code>
        fallback exists
        (<code>{{ fallbackPageId }}</code>).
      </p>
    </div>
  </Page404Shell>
</template>
